import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Upload, Download, Trophy, Search, X, ArrowRight, ArrowLeft, 
  CheckCircle, Image, Grid3X3, Zap
} from 'lucide-react';
import { API } from '../lib/constants';
import { getCollegeColors } from '../lib/collegeColors';

const CreateGraphic = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  
  // Mode
  const [mode, setMode] = useState('offer'); // 'offer' or 'topschools'
  
  // Player info
  const [playerName, setPlayerName] = useState('');
  const [playerPosition, setPlayerPosition] = useState('');
  const [playerClass, setPlayerClass] = useState('');
  const [playerSchool, setPlayerSchool] = useState('');
  const [playerPhoto, setPlayerPhoto] = useState(null);
  const [playerPhotoUrl, setPlayerPhotoUrl] = useState('');
  const [offerUsername, setOfferUsername] = useState('');
  
  // College search
  const [colleges, setColleges] = useState([]);
  const [collegeSearch, setCollegeSearch] = useState('');
  const [filteredColleges, setFilteredColleges] = useState([]);
  
  // Selected colleges
  const [selectedCollege, setSelectedCollege] = useState(null); // For offer mode
  const [selectedColleges, setSelectedColleges] = useState([]); // For top schools mode
  
  // Generated
  const [generated, setGenerated] = useState(false);
  
  // Load colleges
  useEffect(() => {
    axios.get(`${API}/api/colleges`).then(res => {
      setColleges(res.data.colleges || res.data || []);
    }).catch(() => {});
    
    // Pre-fill from profile if logged in
    const uid = localStorage.getItem('userId');
    const uname = localStorage.getItem('username');
    if (uid && uname) {
      setOfferUsername(uname);
      axios.get(`${API}/api/dashboard/${uid}`).then(res => {
        const p = res.data.profile || res.data;
        if (p.name) setPlayerName(p.name);
        if (p.positions?.length) setPlayerPosition(p.positions[0]);
        if (p.class_year) setPlayerClass(String(p.class_year));
        if (p.school) setPlayerSchool(p.school);
        if (p.profile_picture_url) setPlayerPhotoUrl(p.profile_picture_url);
      }).catch(() => {});
    }
  }, []);
  
  // Filter colleges
  useEffect(() => {
    if (!collegeSearch) { setFilteredColleges([]); return; }
    const filtered = colleges.filter(c => 
      c.name?.toLowerCase().includes(collegeSearch.toLowerCase())
    ).slice(0, 8);
    setFilteredColleges(filtered);
  }, [collegeSearch, colleges]);
  
  // Handle photo upload
  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPlayerPhotoUrl(ev.target.result);
      setPlayerPhoto(file);
    };
    reader.readAsDataURL(file);
  };
  
  // Select a college
  const selectCollege = (college) => {
    if (mode === 'offer') {
      setSelectedCollege(college);
      setCollegeSearch('');
    } else {
      if (selectedColleges.find(c => c.id === college.id)) return;
      if (selectedColleges.length >= 10) { toast.error('Max 10 schools'); return; }
      setSelectedColleges(prev => [...prev, college]);
      setCollegeSearch('');
    }
  };
  
  const removeCollege = (id) => {
    setSelectedColleges(prev => prev.filter(c => c.id !== id));
  };

  // ══════════════════════════════════════
  // CANVAS RENDERING — MODERN DESIGNS
  // ══════════════════════════════════════
  const loadImage = (url) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    });
  };

  const generateImage = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = 1080;
    const H = 1350; // 4:5 ratio (Instagram optimal)
    canvas.width = W;
    canvas.height = H;

    // Helper: draw rounded rect
    const roundRect = (x, y, w, h, r) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
    };

    // Helper: parse hex to RGB
    const hexToRgb = (hex) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return { r, g, b };
    };

    if (mode === 'offer' && selectedCollege) {
      // ═══════════════════════════════════
      // MODERN OFFER GRAPHIC
      // Full-bleed photo, school color gradient, glass UI
      // ═══════════════════════════════════
      const colors = getCollegeColors(selectedCollege.name);
      const rgb = hexToRgb(colors.primary);

      // Black base
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, W, H);

      // Full-bleed player photo
      if (playerPhotoUrl) {
        const photo = await loadImage(playerPhotoUrl);
        if (photo) {
          const scale = Math.max(W / photo.width, H / photo.height);
          const dw = photo.width * scale;
          const dh = photo.height * scale;
          ctx.drawImage(photo, (W - dw) / 2, (H - dh) / 2, dw, dh);
        }
      }

      // School color gradient overlay from bottom
      const grad = ctx.createLinearGradient(0, H * 0.2, 0, H);
      grad.addColorStop(0, 'rgba(0,0,0,0.3)');
      grad.addColorStop(0.5, `rgba(${rgb.r},${rgb.g},${rgb.b},0.4)`);
      grad.addColorStop(0.8, `rgba(${rgb.r},${rgb.g},${rgb.b},0.85)`);
      grad.addColorStop(1, `rgba(${Math.floor(rgb.r*0.5)},${Math.floor(rgb.g*0.5)},${Math.floor(rgb.b*0.5)},0.95)`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      // Top dark gradient for text readability
      const topGrad = ctx.createLinearGradient(0, 0, 0, H * 0.3);
      topGrad.addColorStop(0, 'rgba(0,0,0,0.6)');
      topGrad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = topGrad;
      ctx.fillRect(0, 0, W, H * 0.3);

      // Diagonal accent lines
      ctx.strokeStyle = `rgba(${parseInt(colors.secondary.slice(1,3),16)},${parseInt(colors.secondary.slice(3,5),16)},${parseInt(colors.secondary.slice(5,7),16)},0.3)`;
      ctx.lineWidth = 2;
      for (let i = 0; i < 4; i++) {
        ctx.beginPath();
        ctx.moveTo(650 + i * 50, 0);
        ctx.lineTo(650 + i * 50 - 350, H);
        ctx.stroke();
      }

      // Top accent bar
      ctx.fillStyle = colors.primary;
      ctx.fillRect(0, 0, W, 5);

      // "BLESSED TO RECEIVE AN OFFER FROM" - top left
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 28px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('BLESSED TO', 60, 70);
      ctx.fillText('RECEIVE AN', 60, 105);
      ctx.fillText('OFFER FROM', 60, 140);

      // School logo (top right) - circle with logo or initials
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.arc(W - 130, 130, 90, 0, Math.PI * 2);
      ctx.fill();
      
      const logo = selectedCollege.logoUrl ? await loadImage(selectedCollege.logoUrl) : null;
      if (logo) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(W - 130, 130, 82, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(logo, W - 212, 48, 164, 164);
        ctx.restore();
      } else {
        ctx.fillStyle = colors.primary;
        ctx.font = 'bold 52px system-ui, -apple-system, sans-serif';
        ctx.textAlign = 'center';
        const initials = selectedCollege.name?.split(' ').map(w => w[0]).join('').slice(0, 3) || '?';
        ctx.fillText(initials, W - 130, 145);
      }

      // School name — MASSIVE
      ctx.fillStyle = '#FFFFFF';
      ctx.textAlign = 'left';
      const schoolWords = (selectedCollege.name || 'UNIVERSITY').toUpperCase().split(' ');
      ctx.font = 'bold 82px system-ui, -apple-system, sans-serif';
      let nameY = H - 440;
      for (const word of schoolWords) {
        ctx.fillText(word, 60, nameY, W - 120);
        nameY += 90;
      }

      // Accent bar below school name
      ctx.fillStyle = colors.primary;
      ctx.fillRect(60, nameY - 70, 200, 5);

      // Division + Conference
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.font = '22px system-ui, -apple-system, sans-serif';
      ctx.fillText(selectedCollege.division || 'D1', 60, nameY - 40);

      // Glass player info bar
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      roundRect(40, H - 190, W - 80, 90, 16);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.1)';
      ctx.lineWidth = 1;
      roundRect(40, H - 190, W - 80, 90, 16);
      ctx.stroke();

      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 34px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(playerName?.toUpperCase() || 'PLAYER NAME', 70, H - 155);
      
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = '18px system-ui, -apple-system, sans-serif';
      const infoText = [playerPosition, playerClass ? `CLASS OF ${playerClass}` : '', playerSchool?.toUpperCase()].filter(Boolean).join(' · ');
      ctx.fillText(infoText, 70, H - 120);

      // Watermark footer
      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(0, H - 55, W, 55);
      ctx.fillStyle = '#16A34A';
      ctx.font = 'bold 18px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(offerUsername ? `offersz.co/${offerUsername}` : 'offersz.co', W / 2, H - 25);

    } else if (mode === 'topschools' && selectedColleges.length > 0) {
      // ═══════════════════════════════════
      // MODERN TOP SCHOOLS GRAPHIC
      // Blurred bg, glass cards, ranked list
      // ═══════════════════════════════════

      // Dark base
      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(0, 0, W, H);

      // Blurred background photo
      if (playerPhotoUrl) {
        const bgPhoto = await loadImage(playerPhotoUrl);
        if (bgPhoto) {
          const scale = Math.max(W / bgPhoto.width, H / bgPhoto.height) * 1.2;
          const dw = bgPhoto.width * scale;
          const dh = bgPhoto.height * scale;
          ctx.filter = 'blur(12px) brightness(0.3)';
          ctx.drawImage(bgPhoto, (W - dw) / 2, (H - dh) / 2, dw, dh);
          ctx.filter = 'none';
        }
      }

      // Extra dark overlay
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx.fillRect(0, 0, W, H);

      // Header
      ctx.fillStyle = '#16A34A';
      ctx.font = 'bold 28px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('NARROWING IT DOWN', W / 2, 65);

      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 82px system-ui, -apple-system, sans-serif';
      ctx.fillText(`MY TOP ${selectedColleges.length}`, W / 2, 150);

      // Player photo circle
      const centerY = 340;
      if (playerPhotoUrl) {
        const portrait = await loadImage(playerPhotoUrl);
        if (portrait) {
          ctx.save();
          ctx.beginPath();
          ctx.arc(W / 2, centerY, 120, 0, Math.PI * 2);
          ctx.clip();
          const ps = Math.max(240 / portrait.width, 240 / portrait.height);
          ctx.drawImage(portrait, W/2 - portrait.width*ps/2, centerY - portrait.height*ps/2, portrait.width*ps, portrait.height*ps);
          ctx.restore();
          // Lime ring
          ctx.strokeStyle = '#16A34A';
          ctx.lineWidth = 4;
          ctx.beginPath();
          ctx.arc(W / 2, centerY, 124, 0, Math.PI * 2);
          ctx.stroke();
        }
      }

      // School cards - glass ranked list
      const cardStart = 510;
      const cardH = 80;
      const cardGap = 10;

      for (let i = 0; i < selectedColleges.length; i++) {
        const college = selectedColleges[i];
        const colors = getCollegeColors(college.name);
        const y = cardStart + i * (cardH + cardGap);

        // Glass card
        ctx.fillStyle = 'rgba(255,255,255,0.08)';
        roundRect(60, y, W - 120, cardH, 12);
        ctx.fill();

        // Left color accent bar
        ctx.fillStyle = colors.primary;
        roundRect(60, y, 8, cardH, 4);
        ctx.fill();

        // Numbered circle
        ctx.fillStyle = colors.primary;
        ctx.beginPath();
        ctx.arc(110, y + cardH / 2, 24, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 22px system-ui, -apple-system, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(String(i + 1), 110, y + cardH / 2 + 8);

        // College logo
        const cLogo = college.logoUrl ? await loadImage(college.logoUrl) : null;
        if (cLogo) {
          ctx.save();
          ctx.beginPath();
          ctx.arc(180, y + cardH / 2, 25, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(cLogo, 155, y + cardH / 2 - 25, 50, 50);
          ctx.restore();
        }

        // School name
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 26px system-ui, -apple-system, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(college.name?.toUpperCase() || 'SCHOOL', cLogo ? 220 : 150, y + 35);
        
        ctx.fillStyle = 'rgba(255,255,255,0.3)';
        ctx.font = '14px system-ui, -apple-system, sans-serif';
        ctx.fillText(college.division || '', cLogo ? 220 : 150, y + 58);

        // Color dot right side
        ctx.fillStyle = colors.primary;
        ctx.beginPath();
        ctx.arc(W - 100, y + cardH / 2, 8, 0, Math.PI * 2);
        ctx.fill();
      }

      // Player info
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 36px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(playerName?.toUpperCase() || 'PLAYER NAME', W / 2, H - 120);

      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.font = '18px system-ui, -apple-system, sans-serif';
      const info = [playerPosition, playerClass ? `CLASS OF ${playerClass}` : '', playerSchool?.toUpperCase()].filter(Boolean).join(' · ');
      ctx.fillText(info, W / 2, H - 80);

      // Watermark footer
      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(0, H - 55, W, 55);
      ctx.fillStyle = '#16A34A';
      ctx.font = 'bold 18px system-ui, -apple-system, sans-serif';
      ctx.fillText(offerUsername ? `offersz.co/${offerUsername}` : 'offersz.co', W / 2, H - 25);
    }

    setGenerated(true);
  }, [mode, selectedCollege, selectedColleges, playerPhotoUrl, playerName, playerPosition, playerClass, playerSchool, offerUsername]);

  // Download
  const downloadImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = mode === 'offer' 
      ? `offer-${selectedCollege?.name?.replace(/\s/g, '-') || 'graphic'}.png`
      : `my-top-${selectedColleges.length}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Image downloaded!');
  };

  const canGenerate = mode === 'offer' ? !!selectedCollege : selectedColleges.length >= 2;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <div className="bg-[#0a0a0a] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#1a1a1a] rounded-full flex items-center justify-center">
                <span className="text-[#16A34A] font-black text-sm">O</span>
              </div>
              <span className="font-black text-lg">Offer</span>
            </button>
            <span className="text-white/20">|</span>
            <span className="text-white/50 text-sm font-semibold">Graphics</span>
          </div>
          {!localStorage.getItem('userId') && (
            <Button onClick={() => navigate('/create-profile')} className="bg-[#0F5132] text-white font-bold rounded-full text-xs px-4" size="sm">
              Create Profile
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-black mb-2">Create Your Graphic</h1>
          <p className="text-white/40 text-sm">Generate a shareable image for Twitter in seconds.</p>
        </div>

        {/* Mode Toggle */}
        <div className="flex justify-center mb-8">
          <div className="flex bg-white/5 rounded-full p-1 border border-white/10">
            <button onClick={() => { setMode('offer'); setGenerated(false); }}
              className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all flex items-center gap-2 ${
                mode === 'offer' ? 'bg-[#0F5132] text-white' : 'text-white/40'
              }`}>
              <Trophy className="h-4 w-4" /> New Offer
            </button>
            <button onClick={() => { setMode('topschools'); setGenerated(false); }}
              className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all flex items-center gap-2 ${
                mode === 'topschools' ? 'bg-[#0F5132] text-white' : 'text-white/40'
              }`}>
              <Grid3X3 className="h-4 w-4" /> My Top Schools
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* ── LEFT: Form ── */}
          <div className="space-y-5">
            {/* Photo Upload */}
            <div>
              <label className="text-xs text-white/50 font-semibold uppercase tracking-wider mb-2 block">Your Photo</label>
              <label className="block cursor-pointer">
                <div className={`border-2 border-dashed rounded-2xl p-6 text-center transition-all hover:border-[#16A34A]/50 ${
                  playerPhotoUrl ? 'border-[#16A34A]/30' : 'border-white/10'
                }`}>
                  {playerPhotoUrl ? (
                    <div className="flex items-center gap-4 justify-center">
                      <img src={playerPhotoUrl} alt="" className="w-16 h-16 rounded-xl object-cover" />
                      <div className="text-left">
                        <p className="text-[#16A34A] text-sm font-bold">Photo loaded</p>
                        <p className="text-white/30 text-xs">Click to change</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-white/20 mx-auto mb-2" />
                      <p className="text-white/40 text-sm">Upload your photo</p>
                    </>
                  )}
                </div>
                <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
              </label>
            </div>

            {/* Player Info */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-white/50 font-semibold uppercase tracking-wider mb-1 block">Name</label>
                <Input value={playerName} onChange={e => setPlayerName(e.target.value)} placeholder="Your name"
                  className="bg-white/5 border-white/10 text-white h-11 rounded-xl" />
              </div>
              <div>
                <label className="text-xs text-white/50 font-semibold uppercase tracking-wider mb-1 block">Position</label>
                <Input value={playerPosition} onChange={e => setPlayerPosition(e.target.value)} placeholder="QB, WR..."
                  className="bg-white/5 border-white/10 text-white h-11 rounded-xl" />
              </div>
              <div>
                <label className="text-xs text-white/50 font-semibold uppercase tracking-wider mb-1 block">Class Year</label>
                <Input value={playerClass} onChange={e => setPlayerClass(e.target.value)} placeholder="2026"
                  className="bg-white/5 border-white/10 text-white h-11 rounded-xl" />
              </div>
              <div>
                <label className="text-xs text-white/50 font-semibold uppercase tracking-wider mb-1 block">High School</label>
                <Input value={playerSchool} onChange={e => setPlayerSchool(e.target.value)} placeholder="Your school"
                  className="bg-white/5 border-white/10 text-white h-11 rounded-xl" />
              </div>
            </div>

            {/* Offer username */}
            <div>
              <label className="text-xs text-white/50 font-semibold uppercase tracking-wider mb-1 block">Offer Username <span className="text-white/20">(for watermark)</span></label>
              <div className="flex items-center bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                <span className="text-white/20 text-sm pl-3">offersz.co/</span>
                <input value={offerUsername} onChange={e => setOfferUsername(e.target.value)} placeholder="yourname"
                  className="flex-1 bg-transparent text-white text-sm py-2.5 pr-3 outline-none" />
              </div>
            </div>

            {/* College Search */}
            <div>
              <label className="text-xs text-white/50 font-semibold uppercase tracking-wider mb-1 block">
                {mode === 'offer' ? 'Select School' : `Select Schools (${selectedColleges.length}/10)`}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                <input value={collegeSearch} onChange={e => setCollegeSearch(e.target.value)}
                  placeholder="Search colleges..."
                  className="w-full bg-white/5 border border-white/10 text-white pl-10 pr-4 py-2.5 rounded-xl text-sm placeholder:text-white/30 outline-none focus:border-[#16A34A]/50" />
              </div>
              
              {/* Search Results */}
              {filteredColleges.length > 0 && (
                <div className="mt-1 border border-white/10 rounded-xl bg-[#1a1a1a] overflow-hidden max-h-48 overflow-y-auto">
                  {filteredColleges.map(c => (
                    <button key={c.id} onClick={() => selectCollege(c)}
                      className="w-full text-left px-3 py-2.5 hover:bg-white/5 flex items-center gap-3 text-sm border-b border-white/5 last:border-0">
                      {c.logoUrl && <img src={c.logoUrl} className="w-8 h-8 object-contain rounded" alt="" />}
                      <div className="flex-1"><span className="font-semibold">{c.name}</span></div>
                      <span className="text-white/30 text-xs">{c.division}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Selected college(s) */}
              {mode === 'offer' && selectedCollege && (
                <div className="mt-3 flex items-center gap-3 bg-white/5 rounded-xl p-3 border border-[#16A34A]/20">
                  {selectedCollege.logoUrl && <img src={selectedCollege.logoUrl} className="w-10 h-10 object-contain rounded bg-white p-1" alt="" />}
                  <div className="flex-1"><p className="font-bold text-sm">{selectedCollege.name}</p><p className="text-white/30 text-xs">{selectedCollege.division}</p></div>
                  <button onClick={() => setSelectedCollege(null)} className="text-white/30 hover:text-white"><X className="h-4 w-4" /></button>
                </div>
              )}
              
              {mode === 'topschools' && selectedColleges.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedColleges.map(c => (
                    <div key={c.id} className="flex items-center gap-2 bg-white/5 rounded-full pl-1 pr-2 py-1 border border-white/10">
                      {c.logoUrl && <img src={c.logoUrl} className="w-6 h-6 object-contain rounded-full bg-white p-0.5" alt="" />}
                      <span className="text-xs font-semibold">{c.name}</span>
                      <button onClick={() => removeCollege(c.id)} className="text-white/30 hover:text-red-400"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Generate Button */}
            <Button onClick={generateImage} disabled={!canGenerate}
              className="w-full bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold h-12 rounded-xl text-lg disabled:opacity-30">
              <Zap className="h-5 w-5 mr-2" />
              Generate {mode === 'offer' ? 'Offer Graphic' : 'Top Schools Graphic'}
            </Button>
          </div>

          {/* ── RIGHT: Preview ── */}
          <div>
            <label className="text-xs text-white/50 font-semibold uppercase tracking-wider mb-2 block">Preview</label>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 aspect-square flex items-center justify-center overflow-hidden">
              {generated ? (
                <canvas ref={canvasRef} className="w-full h-full object-contain rounded-xl" />
              ) : (
                <div className="text-center">
                  <Image className="h-16 w-16 text-white/10 mx-auto mb-3" />
                  <p className="text-white/20 text-sm">
                    {mode === 'offer' ? 'Select a school and generate' : 'Select 2+ schools and generate'}
                  </p>
                </div>
              )}
            </div>
            
            {generated && (
              <div className="flex gap-3 mt-4">
                <Button onClick={downloadImage} className="flex-1 bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold rounded-xl">
                  <Download className="h-4 w-4 mr-2" /> Download PNG
                </Button>
                <Button onClick={() => {
                  const text = mode === 'offer'
                    ? `Blessed to receive an offer from ${selectedCollege?.name}! 🏈\n\nCreate yours: offersz.co`
                    : `Narrowing it down to my top ${selectedColleges.length}! 🏈\n\nCreate yours: offersz.co`;
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
                }} variant="outline" className="border-white/10 text-white hover:bg-white/5 rounded-xl">
                  Share on X
                </Button>
              </div>
            )}

            {/* Hidden canvas for generation when not yet shown */}
            {!generated && <canvas ref={canvasRef} className="hidden" />}
          </div>
        </div>

        {/* CTA */}
        {!localStorage.getItem('userId') && (
          <div className="mt-12 text-center">
            <div className="bg-[#16A34A]/10 border border-[#16A34A]/20 rounded-2xl p-8">
              <h3 className="font-black text-xl mb-2">Want more than just a graphic?</h3>
              <p className="text-white/40 text-sm mb-4">Create a full Offer profile with highlights, stats, and offers. Free forever.</p>
              <Button onClick={() => navigate('/create-profile')} className="bg-[#0F5132] text-white font-bold rounded-full px-8">
                Create Your Profile <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateGraphic;
